// src/SignIn.js
import React, { useEffect, useState } from "react";
import { login, loginEntra } from "./api";
import {
  entraEnabled,
  takeRedirectResult,
  startEntraLogin,
  clearEntraCache,
} from "./entraAuth";

function errorMessage(err, fallback) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

export default function SignIn({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Password form is the break-glass path when Entra is configured.
  const [showPasswordForm, setShowPasswordForm] = useState(!entraEnabled);

  // Returning from the Entra redirect: exchange the token for a session.
  useEffect(() => {
    const pending = takeRedirectResult();
    if (!pending) return;
    if (pending.error) {
      setError(pending.error);
      return;
    }

    let cancelled = false;
    setBusy(true);
    loginEntra(pending.token)
      .then((data) => {
        if (!cancelled && onLogin) onLogin(data);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, "Microsoft sign-in failed"));
      })
      .finally(() => {
        clearEntraCache();
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleMicrosoft() {
    setError("");
    setBusy(true);
    try {
      await startEntraLogin(); // navigates away on success
    } catch (err) {
      setError(errorMessage(err, "Microsoft sign-in failed"));
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login(email.trim(), password.trim());

      // Backend sets cookie; we pass back the full user payload
      if (onLogin) {
        if (data && typeof data === "object") {
          onLogin(data);
        } else {
          onLogin({ email: email.trim() });
        }
      }
    } catch (err) {
      setError(errorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 420, margin: "15vh auto", textAlign: "center" }}>
      <h2>Sign in to Rohirrim Operations Center</h2>

      {entraEnabled && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleMicrosoft}
            disabled={busy}
          >
            {busy ? "Signing in..." : "Sign in with Microsoft"}
          </button>
        </div>
      )}

      {error && (
        <div style={{ color: "red", fontSize: 14, marginTop: 12 }}>{error}</div>
      )}

      {entraEnabled && !showPasswordForm && (
        <div style={{ marginTop: 24 }}>
          <button
            type="button"
            className="btn btn-link btn-sm text-muted"
            onClick={() => setShowPasswordForm(true)}
          >
            Administrator break-glass sign-in
          </button>
        </div>
      )}

      {showPasswordForm && (
        <>
          <p style={{ marginTop: entraEnabled ? 24 : 0 }}>
            {entraEnabled
              ? "Break-glass sign-in for administrators only."
              : "Enter your credentials."}
          </p>
          <form onSubmit={handleSubmit} style={{ marginTop: 16, textAlign: "left" }}>
            <label style={{ display: "block", marginBottom: 4 }}>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sajatha@rohirrim.ai"
              style={{ width: "100%", padding: 8, marginBottom: 8 }}
            />

            <label style={{ display: "block", marginBottom: 4 }}>Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{ width: "100%", padding: 8, marginBottom: 8 }}
            />

            <button type="submit" disabled={busy} style={{ padding: "6px 16px" }}>
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
